const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 40;
const NAVY = '0.016 0.212 0.388';

const DAY_LABELS = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};

function clubFileSlug(name) {
  return (
    String(name || 'club')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'club'
  );
}

function cell(v) {
  if (v == null || v === '') return '—';
  return String(v);
}

function dayLabel(day) {
  return (
    DAY_LABELS[day] ||
    (day === 'unk' || day === 'undefined' ? 'Unknown' : day || '—')
  );
}

function toLatin1(s) {
  return Array.from(String(s ?? ''))
    .map((ch) => {
      if (ch === '×') return 'x';
      if (ch === '–' || ch === '—') return '-';
      if (ch === '’') return "'";
      const code = ch.charCodeAt(0);
      if (code === 9 || code === 10 || code === 13) return ' ';
      if (code >= 32 && code <= 126) return ch;
      if (code >= 160 && code <= 255) return ch;
      return '?';
    })
    .join('');
}

function pdfEscape(s) {
  return toLatin1(s).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function wrapText(text, maxChars) {
  const raw = toLatin1(text);
  if (raw.length <= maxChars) return [raw || ' '];
  const words = raw.split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length <= maxChars) cur = next;
    else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [' '];
}

class SimplePdf {
  constructor() {
    this.pages = [];
    this.newPage(false);
  }

  newPage(drawHeader = true) {
    this.ops = [];
    this.pages.push(this.ops);
    this.y = PAGE_H - 28;
    if (drawHeader) this.banner();
  }

  ensure(h) {
    if (this.y - h < 48) this.newPage();
  }

  op(s) {
    this.ops.push(s);
  }

  banner() {
    this.op(`${NAVY} rg`);
    this.op(`0 ${PAGE_H - 36} ${PAGE_W} 36 re f`);
    this.op('1 1 1 rg');
    this.text(MARGIN, PAGE_H - 16, 'Helvetic Padel Series', 14, true);
    this.text(MARGIN, PAGE_H - 28, 'Partner club report', 9, false);
    this.op('0.06 0.09 0.16 rg');
    this.y = PAGE_H - 52;
  }

  text(x, y, str, size, bold) {
    const font = bold ? 'F2' : 'F1';
    this.op('BT');
    this.op(`/${font} ${size} Tf`);
    this.op(`${x.toFixed(2)} ${y.toFixed(2)} Td`);
    this.op(`(${pdfEscape(str)}) Tj`);
    this.op('ET');
  }

  title(str, size = 13) {
    this.ensure(20);
    this.op(`${NAVY} rg`);
    this.text(MARGIN, this.y, str, size, true);
    this.y -= size + 6;
    this.op('0.06 0.09 0.16 rg');
  }

  muted(str) {
    this.ensure(14);
    this.op('0.39 0.45 0.55 rg');
    this.text(MARGIN, this.y, str, 9, false);
    this.y -= 14;
    this.op('0.06 0.09 0.16 rg');
  }

  table(head, body, colWeights) {
    const usable = PAGE_W - MARGIN * 2;
    const weights = colWeights || head.map(() => 1);
    const sum = weights.reduce((a, b) => a + b, 0);
    const widths = weights.map((w) => (w / sum) * usable);
    const rows = [head, ...(body.length ? body : [['No data']])];
    const charW = 1.7;
    rows.forEach((row, ri) => {
      const wrapped = row.map((cellVal, i) =>
        wrapText(cellVal, Math.max(8, Math.floor(widths[i] / charW)))
      );
      const lineCount = Math.max(...wrapped.map((l) => l.length), 1);
      const rowH = 8 + lineCount * 10;
      this.ensure(rowH + 2);
      if (ri === 0) {
        this.op(`${NAVY} rg`);
        this.op(`${MARGIN} ${this.y - rowH + 6} ${usable} ${rowH} re f`);
        this.op('1 1 1 rg');
      } else if (ri % 2 === 0) {
        this.op('0.97 0.98 0.99 rg');
        this.op(`${MARGIN} ${this.y - rowH + 6} ${usable} ${rowH} re f`);
        this.op('0.06 0.09 0.16 rg');
      }
      let x = MARGIN + 4;
      wrapped.forEach((lines, i) => {
        lines.forEach((line, li) => {
          this.text(x, this.y - 6 - li * 10, line, 8, ri === 0);
        });
        x += widths[i];
      });
      this.op('0.06 0.09 0.16 rg');
      this.y -= rowH;
    });
    this.y -= 8;
  }

  build() {
    const pageCount = this.pages.length;
    this.pages.forEach((ops, i) => {
      ops.push('0.39 0.45 0.55 rg');
      ops.push('BT');
      ops.push('/F1 8 Tf');
      ops.push(`${MARGIN.toFixed(2)} 22 Td`);
      ops.push(`(${pdfEscape(`Helvetic Padel Series  -  Page ${i + 1} of ${pageCount}`)}) Tj`);
      ops.push('ET');
    });

    const objs = [];
    const add = (body) => {
      objs.push(body);
      return objs.length;
    };

    const font1 = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
    const font2 = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
    const byteLen = (s) => s.length;
    const contentIds = this.pages.map((ops) => {
      const stream = ops.join('\n');
      return add(`<< /Length ${byteLen(stream)} >>\nstream\n${stream}\nendstream`);
    });
    const pageIds = contentIds.map((cid) =>
      add(
        `<< /Type /Page /Parent PAGES /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Resources << /Font << /F1 ${font1} 0 R /F2 ${font2} 0 R >> >> /Contents ${cid} 0 R >>`
      )
    );
    const pagesId = add(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`);
    const catalogId = add(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

    const rebuilt = objs.map((body, idx) => {
      const id = idx + 1;
      if (id === pagesId) return body;
      return body.replace('/Parent PAGES', `/Parent ${pagesId} 0 R`);
    });

    let out = '%PDF-1.4\n';
    const offsets = [0];
    rebuilt.forEach((body, idx) => {
      offsets[idx + 1] = out.length;
      out += `${idx + 1} 0 obj\n${body}\nendobj\n`;
    });
    const xrefAt = out.length;
    out += `xref\n0 ${rebuilt.length + 1}\n`;
    out += '0000000000 65535 f \n';
    for (let i = 1; i <= rebuilt.length; i += 1) {
      out += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
    }
    out += `trailer\n<< /Size ${rebuilt.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefAt}\n%%EOF`;
    return out;
  }
}

export function downloadPartnerClubPdf(report) {
  const club = report?.club || {};
  const period = report?.period || {};
  const u = report?.utilisation || {};
  const peak = u.peakSplit || {};
  const occ = u.occupancy || {};
  const players = report?.players || {};
  const retention = report?.retention || {};
  const peer = report?.peerBenchmark || {};
  const divisions = report?.divisions || {};

  const pdf = new SimplePdf();
  pdf.banner();
  pdf.title(club.name || 'Club');
  pdf.muted(
    [
      club.canton ? `Canton ${club.canton}` : null,
      club.isPartner ? 'Partner club' : 'Not marked partner',
      period.from && period.to ? `${period.from} to ${period.to}` : null,
    ]
      .filter(Boolean)
      .join('  |  ')
  );

  pdf.title('Summary', 11);
  pdf.table(
    ['Metric', 'Value'],
    [
      ['Matches', cell(u.matchCount ?? 0)],
      ['Court hours', cell(u.courtHours)],
      ['Estimated revenue CHF', cell(u.estimatedRevenueChf ?? 'N/A')],
      ['Hourly rate CHF', cell(club.hourlyRateChf)],
      ['Peak matches', cell(peak.peak ?? 'N/A')],
      ['Off-peak matches', cell(peak.offPeak ?? 'N/A')],
      ['Peak share %', cell(peak.peakPct ?? 'N/A')],
      ['Occupancy %', cell(occ.occupancyPct ?? 'N/A')],
      ['HPS hours', cell(occ.hpsHours)],
      ['Available court hours', cell(occ.availableCourtHours)],
      ['Unique players', cell(players.uniquePlayers ?? 0)],
      ['Retention %', cell(retention.retentionPct)],
      ['Retained players', cell(retention.retainedPlayers)],
      ['Prior-period unique players', cell(retention.previousUniquePlayers)],
      ['Peer benchmark', cell(peer.label || 'N/A')],
      ['Partner clubs in benchmark', cell(peer.partnerClubCount)],
    ],
    [1.4, 2]
  );

  if (report?.notAvailable?.length) {
    pdf.title('Metrics needing club setup', 11);
    pdf.table(
      ['Metric', 'Reason'],
      report.notAvailable.map((n) => [cell(n.metric), cell(n.reason)]),
      [1, 2.4]
    );
  }

  pdf.title(
    `Age distribution (known DOB ${players.age?.known ?? 0}, unknown ${players.age?.unknown ?? 0})`,
    11
  );
  pdf.table(
    ['Band', 'Players'],
    (players.age?.bands || []).length
      ? players.age.bands.map((b) => [cell(b.band), cell(b.count)])
      : [['No DOB data', '']],
    [2, 1]
  );

  pdf.title(
    `Gender split (known ${players.sex?.known ?? 0}, unknown ${players.sex?.unknown ?? 0})`,
    11
  );
  pdf.table(
    ['Sex', 'Players'],
    (players.sex?.bands || []).length
      ? players.sex.bands.map((b) => [cell(b.sex), cell(b.count)])
      : [['No sex data', '']],
    [2, 1]
  );

  pdf.title(
    `Division breakdown (${divisions.teamsWithDivision ?? 0} of ${divisions.teamsInMatches ?? 0} teams assigned)`,
    11
  );
  pdf.table(
    ['Division / level', 'Teams'],
    (divisions.bands || []).length
      ? divisions.bands.map((b) => [cell(b.label), cell(b.count)])
      : [['No division assignments', '']],
    [2.4, 1]
  );

  pdf.title('Date x time heatmap', 11);
  pdf.table(
    ['Date', 'Day', 'Hour', 'Matches'],
    (u.heatmap || []).length
      ? u.heatmap.map((h) => [
          cell(h.date),
          dayLabel(h.day),
          h.hour === 'unknown' || h.hour == null ? '—' : `${h.hour}:00`,
          cell(h.count),
        ])
      : [['No matches with venue details in this range', '', '', '']],
    [1.3, 1.3, 0.8, 0.8]
  );

  const raw = pdf.build();
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i) & 0xff;
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const name = clubFileSlug(club.name);
  const from = period.from || '';
  const to = period.to || '';
  a.download = `partner-club-${name}-${from}-to-${to}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
